// Ecommerce → Catálogo Web → Publicación de Productos (/ecommerce/publicacion).
// Capa EDITORIAL sobre el catálogo del ERP: título web, slug, descripción,
// SEO y el switch de publicación. El nombre de inventario NUNCA se expone tal
// cual (error clásico ERP→ecommerce).
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ExternalLink, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Switch, fieldCls } from '@/features/admin/shared';
import { ECOM, ProductoWebT, ecomRpc, usd } from './shared';

export function PublicacionWeb() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [soloPub, setSoloPub] = useState(false);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [form, setForm] = useState({ titulo: '', slug: '', descripcion: '', seo_titulo: '', seo_descripcion: '', destacado: false, orden: '100' });

  const { data: productos = [] } = useQuery<ProductoWebT[]>({
    queryKey: ['ecom-prods', q, soloPub],
    queryFn: () => ecomRpc<ProductoWebT[]>('ecom_productos_web', { _q: q || null, _solo_publicados: soloPub }),
    staleTime: 0, refetchOnMount: 'always',
  });
  const { data: cfg } = useQuery<{ tienda: { slug: string; activo: boolean } | null }>({
    queryKey: ['ecom-config'],
    queryFn: () => ecomRpc('ecom_config_get', {}),
  });

  const guardar = async (p: ProductoWebT, extra: Record<string, unknown> = {}) => {
    try {
      await ecomRpc('ecom_producto_web_guardar', {
        _producto: p.producto_id,
        _data: {
          titulo: form.titulo || p.titulo || undefined, slug: form.slug || p.slug || undefined,
          descripcion: form.descripcion, seo_titulo: form.seo_titulo, seo_descripcion: form.seo_descripcion,
          destacado: form.destacado, orden: Number(form.orden) || 100, ...extra,
        },
      });
      void qc.invalidateQueries({ queryKey: ['ecom-prods'] });
      toast.success('Producto web guardado');
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  const abrir = (p: ProductoWebT) => {
    setAbierto(abierto === p.producto_id ? null : p.producto_id);
    setForm({
      titulo: p.titulo ?? '', slug: p.slug ?? '', descripcion: p.descripcion ?? '',
      seo_titulo: p.seo_titulo ?? '', seo_descripcion: p.seo_descripcion ?? '',
      destacado: p.destacado, orden: String(p.orden ?? 100),
    });
  };

  const publicados = productos.filter((p) => p.publicado).length;
  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Globe className="w-5 h-5" /> Publicación de Productos</h1>
        {cfg?.tienda && (
          <a className="text-sm text-primary inline-flex items-center gap-1 hover:underline"
             href={`/tienda/${cfg.tienda.slug}`} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-3.5 h-3.5" /> Ver tienda {cfg.tienda.activo ? '' : '(inactiva)'}
          </a>
        )}
        <span className="text-sm text-muted-foreground ml-auto">{publicados} publicados / {productos.length}</span>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3 flex-wrap">
            <CardTitle className="text-base">Catálogo</CardTitle>
            <input className={`${fieldCls} !w-72`} placeholder="Buscar producto…" value={q} onChange={(e) => setQ(e.target.value)} />
            <Switch checked={soloPub} onChange={setSoloPub} label="Solo publicados" />
          </div>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {productos.map((p) => (
            <div key={p.producto_id} className="border rounded-md">
              <div className="px-3 py-2 flex items-center gap-3 text-sm">
                <button onClick={() => abrir(p)} className="shrink-0">
                  {abierto === p.producto_id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{p.titulo ?? p.nombre}</span>
                  <span className="text-xs text-muted-foreground"> · {p.codigo} · ERP: {p.nombre}</span>
                </div>
                {!p.tiene_imagen && <span className="text-xs text-amber-600" title="Sin foto: en la tienda sale un placeholder">sin foto</span>}
                <span className="font-mono">{usd(p.precio_web ?? p.precio_base)}</span>
                <span className={`text-xs ${Number(p.disponible) > 0 ? 'text-green-600' : 'text-red-500'}`}>
                  {Number(p.disponible ?? 0) >= 9999 ? '∞' : Number(p.disponible ?? 0).toFixed(0)} disp.
                </span>
                {p.destacado && <span className="text-xs">⭐</span>}
                <Switch checked={p.publicado} label=""
                        onChange={(v) => { abrir(p); void guardar(p, { publicado: v }); }} />
              </div>
              {abierto === p.producto_id && (
                <div className="px-3 pb-3 border-t pt-3 grid gap-3 md:grid-cols-2">
                  <Campo label="Título web (lo que ve el cliente)">
                    <input className={fieldCls} value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} />
                  </Campo>
                  <Campo label="Slug (URL; vacío = automático)">
                    <input className={`${fieldCls} font-mono`} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
                  </Campo>
                  <Campo label="Descripción larga" className="md:col-span-2">
                    <textarea className={fieldCls} rows={4} value={form.descripcion}
                              onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
                  </Campo>
                  <Campo label={`SEO título (${form.seo_titulo.length}/70)`}>
                    <input className={fieldCls} maxLength={70} value={form.seo_titulo}
                           onChange={(e) => setForm({ ...form, seo_titulo: e.target.value })} />
                  </Campo>
                  <Campo label={`SEO descripción (${form.seo_descripcion.length}/170)`}>
                    <input className={fieldCls} maxLength={170} value={form.seo_descripcion}
                           onChange={(e) => setForm({ ...form, seo_descripcion: e.target.value })} />
                  </Campo>
                  <div className="flex items-center gap-4">
                    <Switch checked={form.destacado} onChange={(v) => setForm({ ...form, destacado: v })} label="Destacado (home)" />
                    <Campo label="Orden">
                      <input className={`${fieldCls} !w-20`} type="number" value={form.orden}
                             onChange={(e) => setForm({ ...form, orden: e.target.value })} />
                    </Campo>
                  </div>
                  <div className="flex items-end justify-end gap-2">
                    <Button size="sm" onClick={() => void guardar(p)}>Guardar</Button>
                  </div>
                  <p className="md:col-span-2 text-xs text-muted-foreground">
                    Las fotos se gestionan en el producto del INVENTARIO (imagen + thumb). El precio web usa la
                    lista de precios de la tienda (Configuración) o el precio base.
                  </p>
                </div>
              )}
            </div>
          ))}
          {!productos.length && <p className="py-6 text-center text-sm text-muted-foreground">Sin productos que coincidan.</p>}
        </CardContent>
      </Card>
    </div>
  );
}
export { ECOM };
